import {
    type CallExpressionShape,
    IdentifierShape,
    ObjectShape,
    type Shape,
    VariableStatementShape,
} from '@servicenow/sdk-build-core'
import { ArrowFunctionShape } from '@servicenow/sdk-build-plugins'
import type { Database, Record as SdkRecord } from '@servicenow/sdk-build-core'

export interface ChildCollectionToShapeConfig {
    /** Parent record (used as AST source node) */
    source: SdkRecord
    /** Ordered child records to transform */
    childRecords: SdkRecord[]
    /** Database handle for child record lookups */
    database: Database
    /** Variable name prefix (e.g. 'lane' produces lane_0, lane_1, ...) */
    varPrefix: string
    /** Callback that converts a single child record into a CallExpressionShape */
    recordToShape: (
        record: SdkRecord,
        database: Database,
        guidToVarName: Map<string, string>
    ) => { success: true; value: CallExpressionShape } | { success: false }
    /**
     * Optional callback that extracts dependency GUIDs from a child record.
     * When provided, records are topologically sorted so that dependencies
     * are declared before the records that reference them (e.g. Run.After).
     */
    getDependencyGuids?: (record: SdkRecord) => string[]
}

/**
 * Topologically sort records so that dependencies come before dependents.
 * Falls back to original order for records with no dependency relationships.
 */
function topologicalSort(records: SdkRecord[], getDependencyGuids: (record: SdkRecord) => string[]): SdkRecord[] {
    const guidToRecord = new Map<string, SdkRecord>()
    for (const record of records) {
        guidToRecord.set(record.getId().getValue(), record)
    }

    const sorted: SdkRecord[] = []
    const visited = new Set<string>()
    const visiting = new Set<string>()

    function visit(record: SdkRecord) {
        const guid = record.getId().getValue()
        if (visited.has(guid)) {
            return
        }
        if (visiting.has(guid)) {
            return // cycle — break it by skipping
        }
        visiting.add(guid)

        for (const depGuid of getDependencyGuids(record)) {
            const depRecord = guidToRecord.get(depGuid)
            if (depRecord) {
                visit(depRecord)
            }
        }

        visiting.delete(guid)
        visited.add(guid)
        sorted.push(record)
    }

    for (const record of records) {
        visit(record)
    }

    return sorted
}

/**
 * Build an ArrowFunctionShape representing a collection of child records.
 *
 * Three-pass approach:
 * 1. Topologically sort records so dependencies are declared first (if getDependencyGuids provided)
 * 2. Build guid→varName map so sibling cross-references (e.g. Run.After) can resolve
 * 3. Convert each child record to a shape using the provided callback
 *
 * Returns a concise `() => ({})` for empty collections, or a block body with
 * variable declarations + return object for non-empty collections.
 */
export function buildChildCollectionToShape(config: ChildCollectionToShapeConfig): ArrowFunctionShape {
    const { source, childRecords, database, varPrefix, recordToShape, getDependencyGuids } = config

    // Sort records so dependencies come before dependents
    const sortedRecords = getDependencyGuids ? topologicalSort(childRecords, getDependencyGuids) : childRecords

    // First pass: build guid → varName map so cross-references can resolve
    const guidToVarName = new Map<string, string>()
    for (const [i, record] of sortedRecords.entries()) {
        guidToVarName.set(record.getId().getValue(), `${varPrefix}_${i}`)
    }

    // Second pass: build shapes, tracking original indices so variable names
    // stay consistent with guidToVarName even when some records fail to convert
    const childShapesWithIndex: { shape: CallExpressionShape; originalIndex: number }[] = []
    for (const [i, record] of sortedRecords.entries()) {
        const result = recordToShape(record, database, guidToVarName)
        if (result.success) {
            childShapesWithIndex.push({ shape: result.value, originalIndex: i })
        }
    }

    // Empty collection: concise form `() => ({})`
    if (childShapesWithIndex.length === 0) {
        return new ArrowFunctionShape({
            source,
            parameters: [],
            returnValue: new ObjectShape({ source, properties: {} }),
            implicitReturn: true,
        })
    }

    // Non-empty: variable declarations + return object
    const statements: Shape[] = []
    const returnEntries: Record<string, Shape> = {}
    for (const { shape, originalIndex } of childShapesWithIndex) {
        const varName = `${varPrefix}_${originalIndex}`
        statements.push(new VariableStatementShape({ source, variableName: varName, initializer: shape }))
        returnEntries[varName] = new IdentifierShape({ source, name: varName })
    }

    const returnValue = new ObjectShape({ source, properties: returnEntries })

    return new ArrowFunctionShape({
        source,
        parameters: [],
        statements,
        returnValue,
    })
}
